import React, { useEffect, useState } from 'react';
import { Wand2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { useImagePromptJob } from '@/hooks/useImagePromptJob';
import { MultiImageUploader } from '../MultiImageUploader';
import { ImageModelSelector } from '../ImageModelSelector';
import { ResolutionSelector } from '../ResolutionSelector';
import { StyleSelector } from '../StyleSelector';
import { ImageResultDisplay } from '../ImageResultDisplay';
import type { ImagePromptInput } from '@/types';

export const ImagePromptTab: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  // Form state
  const [images, setImages] = useState<ImagePromptInput[]>([]);
  const [model, setModel] = useState<string | undefined>();
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [resolution, setResolution] = useState('1152x896');
  const [customWidth, setCustomWidth] = useState<number | undefined>();
  const [customHeight, setCustomHeight] = useState<number | undefined>();
  const [styles, setStyles] = useState<string[]>([]);

  // Async generation (job + polling) — fixes the old synchronous freeze.
  const { isGenerating, progress, message, result, error, processingTime, run, reset } =
    useImagePromptJob();

  // Surface job failures as a toast (no silent freeze).
  useEffect(() => {
    if (error) {
      showToast(
        'error',
        t('image.notifications.errorTitle', { defaultValue: 'Generation Failed' }),
        error
      );
    }
  }, [error, showToast, t]);

  useEffect(() => {
    if (result && result.length > 0) {
      showToast(
        'success',
        t('image.notifications.successTitle', { defaultValue: 'Images Ready!' }),
        t('image.imagePrompt.result.processingTime', {
          defaultValue: 'Generated in {{time}} seconds',
          time: (processingTime || 0).toFixed(1),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const handleResolutionChange = (preset: string, width?: number, height?: number) => {
    setResolution(preset);
    setCustomWidth(width);
    setCustomHeight(height);
  };

  const handleGenerate = async () => {
    const validImages = images.filter((img) => img.image);
    if (validImages.length === 0) {
      showToast(
        'error',
        t('common.error', { defaultValue: 'Error' }),
        t('image.imagePrompt.noImagesWarning', {
          defaultValue: 'Please upload at least one reference image',
        })
      );
      return;
    }

    await run({
      prompt,
      negative_prompt: negativePrompt,
      images: validImages,
      styles,
      model,
      resolution:
        resolution === 'custom' && customWidth && customHeight
          ? `${customWidth}x${customHeight}`
          : resolution,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isGenerating && !result && (
        <>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('image.imagePrompt.title', { defaultValue: 'Image to Image' })}
            </h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
              {t('image.imagePrompt.description', {
                defaultValue: 'Generate images using reference images for style and composition',
              })}
            </p>
          </div>

          {/* Checkpoint model selector (reuses the standard Fooocus selector) */}
          <ImageModelSelector
            value={model}
            onChange={setModel}
            provider="fooocus"
            disabled={isGenerating}
          />

          {/* Multi-image uploader (Fooocus ControlNet supports up to 4 refs) */}
          <div>
            <label className="label-gray-alt">
              {t('image.imagePrompt.uploadImages', { defaultValue: 'Reference Images' })}
            </label>
            <p className="text-xs text-gray-500 dark:text-secondary-400 mb-2">
              {t('image.imagePrompt.uploadHint', {
                defaultValue:
                  'Fooocus blends up to 4 reference images (style, pose & composition).',
              })}
            </p>
            <MultiImageUploader
              images={images}
              onChange={setImages}
              maxImages={4}
              disabled={isGenerating}
            />
          </div>

          {/* Text prompt */}
          <div>
            <label htmlFor="prompt" className="label-gray-alt">
              {t('image.imagePrompt.prompt', { defaultValue: 'Prompt' })}
            </label>
            <textarea
              id="prompt"
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('image.imagePrompt.promptPlaceholder', {
                defaultValue: 'Describe what you want to generate...',
              })}
              disabled={isGenerating}
              className="input resize-none"
            />
          </div>

          {/* Negative prompt */}
          <div>
            <label htmlFor="negative-prompt" className="label-gray-alt">
              {t('image.negativePrompt.label', { defaultValue: 'Negative Prompt' })}
              <span className="text-gray-500 dark:text-secondary-400 ml-1">
                ({t('common.optional', { defaultValue: 'optional' })})
              </span>
            </label>
            <textarea
              id="negative-prompt"
              rows={2}
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder={t('image.negativePrompt.placeholder', {
                defaultValue: 'What to avoid in the image...',
              })}
              disabled={isGenerating}
              className="input resize-none"
            />
          </div>

          {/* Resolution selector */}
          <ResolutionSelector
            value={resolution}
            customWidth={customWidth}
            customHeight={customHeight}
            onChange={handleResolutionChange}
            disabled={isGenerating}
          />

          {/* Style selector */}
          <StyleSelector selectedStyles={styles} onChange={setStyles} disabled={isGenerating} />

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating || images.every((img) => !img.image)}
            className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Wand2 className="w-5 h-5 mr-2" />
            {isGenerating
              ? t('image.imagePrompt.generating', { defaultValue: 'Generating...' })
              : t('image.imagePrompt.generate', { defaultValue: 'Generate' })}
          </button>
        </>
      )}

      {/* Loading state with progress (cold-start aware) */}
      {isGenerating && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-secondary-400">
            {message || t('image.imagePrompt.generating', { defaultValue: 'Generating...' })}
          </p>
          <p className="mt-2 text-xs text-gray-500 dark:text-secondary-500">
            {t('image.imagePrompt.firstRunNote', {
              defaultValue:
                'The first Image-to-Image run can take a few minutes (one-time setup). Later runs are fast.',
            })}
          </p>
          {progress > 0 && (
            <div className="w-full max-w-md mx-auto mt-4 bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {result && result.length > 0 && !isGenerating && (
        <ImageResultDisplay images={result} processingTime={processingTime || 0} onReset={reset} />
      )}
    </div>
  );
};

export default ImagePromptTab;
